import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, Loader2, Rocket } from 'lucide-react';
import { Input } from '../../../../components/ui/input';
import { Switch } from '../../../../components/ui/switch';
import { api } from '../../../../lib/api';
import { useToast } from '../../../../hooks/use-toast';
import CoverImagePanel from '../../../../components/admin/CoverImagePanel';
import { Field, inputCls, wordCount } from '../shared';

const StepPublish = ({ wf, setData, save }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const d = wf.data?.publish || {};
  const seo = wf.data?.seo || {};
  const a = wf.article || {};
  const [busy, setBusy] = useState(false);
  const tags = d.tags ?? seo.tags ?? [];

  const publish = async (published) => {
    setBusy(true);
    try {
      await save();
      const res = await api.post(`/admin/workflows/${wf.id}/publish`, {
        published,
        featured: !!d.featured,
        tags,
        author: d.author || 'BriefingIQ Team',
        cover_image: d.cover_image || null,
        cover_history: d.cover_history || [],
        alt_text: d.alt_text || '',
      });
      toast({
        title: published ? 'Post published' : 'Draft post created',
        description: published ? 'It is live on the blog. Opening the post editor.' : 'Opening the post editor.',
      });
      navigate(`/admin/blog/${res.data.id}`);
    } catch (err) {
      toast({ title: 'Publish failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const rows = [
    ['Title', a.title],
    ['URL', `/blog/${seo.slug || '—'}`],
    ['SEO title', seo.seo_title],
    ['Meta description', seo.seo_description],
    ['Excerpt', seo.excerpt || a.excerpt],
    ['Length', `${wordCount(a.content)} words`],
    ['FAQ schema', (wf.data?.jsonld?.faq?.length && wf.data?.jsonld?.include_faq !== false) ? `${wf.data.jsonld.faq.length} questions` : 'none'],
  ];

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-[var(--line-2)] p-5">
        <CoverImagePanel
          value={{ cover_image: d.cover_image || '', cover_history: d.cover_history || [], alt_text: d.alt_text || '' }}
          onChange={(patch) => setData('publish', patch)}
          context={{ title: a.title, excerpt: seo.excerpt || a.excerpt, tags }}
        />
      </div>

      <div className="rounded-2xl border border-[var(--line-2)] divide-y divide-[var(--line-2)]" data-testid="publish-summary">
        {rows.map(([k, v]) => (
          <div key={k} className="grid grid-cols-[160px,1fr] gap-4 px-5 py-3 text-sm">
            <div className="text-ink-3 text-[11px] uppercase tracking-wider pt-0.5">{k}</div>
            <div className="text-ink break-words">{v || <span className="text-ink-3">—</span>}</div>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Tags" hint="Comma separated." testId="publish-tags-field">
          <Input value={tags.join(', ')} onChange={(e) => setData('publish', { tags: e.target.value.split(',').map((t) => t.trim()).filter(Boolean) })} className={inputCls} data-testid="publish-tags-input" />
        </Field>
        <Field label="Author" testId="publish-author-field">
          <Input value={d.author || 'BriefingIQ Team'} onChange={(e) => setData('publish', { author: e.target.value })} className={inputCls} />
        </Field>
      </div>
      <label className="flex items-center justify-between rounded-2xl border border-[var(--line-2)] p-4">
        <div>
          <div className="text-sm font-medium text-ink">Feature on the Blog page</div>
          <div className="text-[11px] text-ink-3">Pins this post as the large card. Only one post can be featured.</div>
        </div>
        <Switch checked={!!d.featured} onCheckedChange={(v) => setData('publish', { featured: v })} data-testid="publish-featured-switch" />
      </label>

      <div className="pt-4 border-t border-[var(--line-2)] flex items-center justify-between flex-wrap gap-3">
        {wf.post_id ? (
          <button type="button" onClick={() => navigate(`/admin/blog/${wf.post_id}`)} className="btn-link" data-testid="publish-open-post-btn">
            <ExternalLink className="w-4 h-4" /> Open existing post
          </button>
        ) : <span />}
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => publish(false)} disabled={busy} className="btn-secondary disabled:opacity-60" data-testid="publish-draft-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {wf.post_id ? 'Update as draft' : 'Save as draft post'}
          </button>
          <button type="button" onClick={() => publish(true)} disabled={busy} className="btn-primary disabled:opacity-60" data-testid="publish-now-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} {wf.post_id ? 'Update & publish' : 'Publish post'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StepPublish;
